import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Footer from '../components/Footer'
import { fetchEmployeeAppointments } from '../services/appointmentsApi'
import { acceptAppointment, rejectAppointment } from '../services/appointmentsApi'
import { getAuthToken, getAuthUser, logout } from '../services/authService'
import { useSocket } from '../hooks/useSocket'
import { useToast } from '../components/ToastProvider'
import { requestNotificationPermission, registerInstallation, onMessageReceived } from '../services/fcmService'
import { apiUrl } from '../services/apiConfig'

export default function EmployeeDashboard() {
  const navigate = useNavigate()
  const toast = useToast()
  const { socket, isConnected } = useSocket()
  const [employee, setEmployee] = useState(null)
  const [employeeDocumentId, setEmployeeDocumentId] = useState(null)
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedAppointment, setSelectedAppointment] = useState(null)
  const [selectedDetailsAppointment, setSelectedDetailsAppointment] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [actionLoading, setActionLoading] = useState(null)
  const [rejectionModal, setRejectionModal] = useState(false)
  const [detailsModal, setDetailsModal] = useState(false)

  useEffect(() => {
    // Check if user is authenticated
    const token = getAuthToken()
    const user = getAuthUser()

    if (!token || !user) {
      navigate('/employee-login')
      return
    }

    const storedEmployeeDocumentId = user.employeeDocumentId || user.employee_document_id || null
    setEmployee(user)

    if (storedEmployeeDocumentId) {
      setEmployeeDocumentId(storedEmployeeDocumentId)
      loadAppointments(storedEmployeeDocumentId)
      return
    }

    fetch(apiUrl('/employees/active', { search: user.email }))
      .then(res => {
        if (!res.ok) {
          console.error('Failed to fetch employees:', res.status, res.statusText)
          loadAppointments(user.id)
          return null
        }
        return res.json()
      })
      .then(data => {
        if (!data) return
        const employees = Array.isArray(data) ? data : data.employees || []
        const match = employees.find(e => e.email?.toLowerCase() === user.email?.toLowerCase())
        const empId = match ? match.id : user.id
        setEmployeeDocumentId(empId)
        loadAppointments(empId)
      })
      .catch(err => {
        console.error('Error fetching employees:', err)
        loadAppointments(user.id)
      })
  }, [navigate])

  const loadAppointments = async (employeeId) => {
    try {
      setLoading(true)
      const data = await fetchEmployeeAppointments(employeeId)
      setAppointments(data)
    } catch (err) {
      toast.error('Error loading appointments')
      console.error('Error loading appointments:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    if (employeeDocumentId) {
      loadAppointments(employeeDocumentId)
      toast.success('Appointments refreshed')
    }
  }

  const handleAccept = async (appointmentId) => {
    try {
      setActionLoading(appointmentId)
      await acceptAppointment(appointmentId)
      setAppointments(appointments.map(apt =>
        apt.id === appointmentId ? { ...apt, status: 'accepted' } : apt
      ))
      toast.success('Appointment accepted')
    } catch (err) {
      toast.error('Failed to accept appointment')
      console.error('Error accepting appointment:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const handleRejectClick = (appointment) => {
    setSelectedAppointment(appointment)
    setRejectReason('')
    setRejectionModal(true)
  }

  const handleDetailsClick = (appointment) => {
    setSelectedDetailsAppointment(appointment)
    setDetailsModal(true)
  }

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please enter rejection remarks')
      return
    }

    try {
      setActionLoading(selectedAppointment.id)
      await rejectAppointment(selectedAppointment.id, rejectReason)
      setAppointments(appointments.map(apt =>
        apt.id === selectedAppointment.id ? { ...apt, status: 'rejected', rejection_reason: rejectReason } : apt
      ))
      setRejectionModal(false)
      setSelectedAppointment(null)
      toast.success('Appointment rejected')
    } catch (err) {
      toast.error(err.message || 'Failed to reject appointment')
      console.error('Error rejecting appointment:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const handleLogout = () => {
    logout()
    toast.success('Logged out successfully')
    navigate('/')
  }

  const closeDetailsModal = () => {
    setDetailsModal(false)
    setSelectedDetailsAppointment(null)
  }

  useEffect(() => {
    if (!socket || !isConnected || !employeeDocumentId) return

    socket.emit('join-employee-room', employeeDocumentId)

    const reloadAppointments = () => loadAppointments(employeeDocumentId)

    socket.on('visitor-registered', reloadAppointments)
    socket.on('new-notification', reloadAppointments)
    socket.on('appointment-accepted', reloadAppointments)
    socket.on('appointment-rejected', reloadAppointments)
    socket.on('visitor-arrived', reloadAppointments)
    socket.on('visitor-checked-in', reloadAppointments)
    socket.on('visitor-checked-out', reloadAppointments)

    return () => {
      socket.off('visitor-registered', reloadAppointments)
      socket.off('new-notification', reloadAppointments)
      socket.off('appointment-accepted', reloadAppointments)
      socket.off('appointment-rejected', reloadAppointments)
      socket.off('visitor-arrived', reloadAppointments)
      socket.off('visitor-checked-in', reloadAppointments)
      socket.off('visitor-checked-out', reloadAppointments)
    }
  }, [socket, isConnected, employeeDocumentId])

  useEffect(() => {
    if (!employee || !employeeDocumentId) return

    const setupNotifications = async () => {
      try {
        const hasPermission = await requestNotificationPermission()
        if (hasPermission) {
          await registerInstallation(employeeDocumentId, 'employee')
        }
      } catch (error) {
        console.error('Error setting up notifications:', error)
      }
    }

    setupNotifications()

    // Refresh when a push arrives (it is shown in the notification panel by the service worker)
    return onMessageReceived((payload) => {
      console.log('Foreground notification:', payload)
      loadAppointments(employeeDocumentId)
    })
  }, [employee, employeeDocumentId])

  if (!employee) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  const pendingCount = appointments.filter(a => a.status === 'pending').length
  const acceptedCount = appointments.filter(a => a.status === 'accepted').length
  const rejectedCount = appointments.filter(a => a.status === 'rejected').length
  const checkedInCount = appointments.filter(a => a.status === 'checked_in').length

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-white to-saffron-50">
      <header className="bg-white shadow-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="igus Logo" className="h-10 w-auto" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">My Dashboard</h1>
              <p className="text-sm text-gray-600">
                {employee.name}
                {employee.designation && <span> • {employee.designation}</span>}
                {employee.department && <span> • {employee.department}</span>}
                {employee.employee_id && <span className="text-gray-400"> (ID: {employee.employee_id})</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-6xl w-full px-4 py-8 sm:px-6">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Visitor Appointments</h2>
          <p className="mt-1 text-gray-600">Review and manage your scheduled visitor appointments</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="mt-2 text-3xl font-bold text-yellow-600">{pendingCount}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Accepted</p>
                <p className="mt-2 text-3xl font-bold text-green-600">{acceptedCount}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Rejected</p>
                <p className="mt-2 text-3xl font-bold text-red-600">{rejectedCount}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l-2-2m0 0l-2-2m2 2l2-2m-2 2l-2 2m2-2l2 2" />
                </svg>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Checked In</p>
                <p className="mt-2 text-3xl font-bold text-blue-600">{checkedInCount}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2 1m2-1l-2-1m2 1v2.5" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Appointments Table */}
        <div className="rounded-xl bg-white shadow-sm ring-1 ring-saffron-200 overflow-hidden">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading appointments...</div>
            ) : appointments.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No appointments scheduled yet</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Visitor Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Organization</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Purpose</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Appointment Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {appointments.map((appointment) => (
                    <tr key={appointment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{appointment.full_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{appointment.organization}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{appointment.purpose}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {appointment.appointment_date} {appointment.appointment_time}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            appointment.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              appointment.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                appointment.status === 'checked_in' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                          }`}>
                          {appointment.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        <button
                          onClick={() => handleDetailsClick(appointment)}
                          className="rounded-lg border border-saffron-300 px-3 py-1.5 text-xs font-medium text-saffron-700 hover:bg-saffron-50"
                        >
                          View Details
                        </button>
                        {appointment.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleAccept(appointment.id)}
                              disabled={actionLoading === appointment.id}
                              className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                            >
                              {actionLoading === appointment.id ? 'Accepting...' : 'Accept'}
                            </button>
                            <button
                              onClick={() => handleRejectClick(appointment)}
                              disabled={actionLoading === appointment.id}
                              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {appointment.status === 'accepted' && (
                          <button
                            onClick={() => handleRejectClick(appointment)}
                            disabled={actionLoading === appointment.id}
                            className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        )}
                        {appointment.status === 'rejected' && (
                          <span className="text-xs text-gray-500">
                            Rejected: {appointment.rejection_reason}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {detailsModal && selectedDetailsAppointment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white shadow-lg">
            <div className="flex items-center justify-between border-b border-gray-200 p-6">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Visitor Details</h2>
                <p className="text-sm text-gray-500">All information for the selected appointment</p>
              </div>
              <button onClick={closeDetailsModal} className="text-gray-400 hover:text-gray-600">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid gap-6 p-6 md:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Visitor Name</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.full_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Organization</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.organization || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Email</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Phone</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Gender</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.gender || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Purpose</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.purpose || 'N/A'}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Appointment Date</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.appointment_date || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Appointment Time</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.appointment_time || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Status</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.status || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Reason</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.reason || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Additional Notes</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.additional_notes || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Created At</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.created_at || 'N/A'}</p>
                </div>
              </div>

              <div className="md:col-span-2">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Signature</p>
                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  {selectedDetailsAppointment.signature ? (
                    selectedDetailsAppointment.signature.startsWith('data:image') ? (
                      <img
                        src={selectedDetailsAppointment.signature}
                        alt="Visitor signature"
                        className="max-h-40 w-full object-contain"
                      />
                    ) : (
                      <pre className="whitespace-pre-wrap break-all text-xs text-gray-700">
                        {selectedDetailsAppointment.signature}
                      </pre>
                    )
                  ) : (
                    <p className="text-sm text-gray-500">No signature available</p>
                  )}
                </div>
              </div>

              <div className="md:col-span-2 flex justify-end">
                <button
                  onClick={closeDetailsModal}
                  className="rounded-lg bg-saffron-600 px-4 py-2 text-sm font-medium text-white hover:bg-saffron-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectionModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Reject Appointment</h2>
              <button
                onClick={() => {
                  setRejectionModal(false)
                  setSelectedAppointment(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600">Visitor: <span className="font-medium text-gray-900">{selectedAppointment.full_name}</span></p>
                <p className="text-sm text-gray-600 mt-1">Organization: <span className="font-medium text-gray-900">{selectedAppointment.organization}</span></p>
              </div>

              <div>
                <label htmlFor="remarks" className="mb-2 block text-sm font-medium text-gray-700">
                  Rejection Remarks <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="remarks"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please provide reason for rejection..."
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleRejectSubmit}
                  disabled={actionLoading === selectedAppointment.id || !rejectReason.trim()}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading === selectedAppointment.id ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
                <button
                  onClick={() => {
                    setRejectionModal(false)
                    setSelectedAppointment(null)
                  }}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  )
}
